/**
 * Helper function to sort coupons: app coupons last
 * This ensures that web/store coupons are shown first, followed by app-only coupons
 */
export function sortCouponsAppLast<T extends { platformChannels?: string[] }>(
	coupons: T[],
): T[] {
	return [
		...coupons.filter((c) => !c.platformChannels?.includes("app")),
		...coupons.filter((c) => c.platformChannels?.includes("app")),
	]
}

/**
 * Helper function to sort coupons: applicable first (web/shop), then others
 * This ensures that web/shop coupons are shown first, followed by other channels
 */
export function sortCouponsApplicableFirst<
	T extends { platformChannels?: string[] },
>(coupons: T[]): T[] {
	return [
		...coupons.filter(
			(c) =>
				c.platformChannels?.includes("shop") ||
				c.platformChannels?.includes("web"),
		),
		...coupons.filter(
			(c) =>
				!(
					c.platformChannels?.includes("shop") ||
					c.platformChannels?.includes("web")
				),
		),
	]
}

/**
 * Helper function to sort coupons with four tiers:
 * 1. Usable coupons (web/shop and not disabled) - sorted by expiry date (soonest first)
 * 2. Haven't met minimum subtotal (web/shop but cart doesn't meet minimum) - sorted by expiry date (soonest first)
 * 3. Not started coupons (web/shop but not yet started) - sorted by expiry date (soonest first)
 * 4. App-only and other non-applicable coupons - sorted by expiry date (soonest first)
 */
export function sortCouponsWithNotStarted<
	T extends {
		platformChannels?: string[]
		couponTemplate?: { startTime?: Date | null; endTime?: Date | null } | null
		expiresAt?: Date | string | null
		appliedDiscountRule?: { minAmount?: number | null } | null
	},
>(coupons: T[], cartSubtotal?: number): T[] {
	const now = new Date()

	// Helper function to get expiry date for sorting
	const getExpiryDate = (coupon: T): Date => {
		// Try couponTemplate.endTime first, then expiresAt
		if (coupon.couponTemplate?.endTime) {
			return coupon.couponTemplate.endTime instanceof Date
				? coupon.couponTemplate.endTime
				: new Date(coupon.couponTemplate.endTime)
		}
		if (coupon.expiresAt) {
			return coupon.expiresAt instanceof Date
				? coupon.expiresAt
				: new Date(coupon.expiresAt)
		}
		// Fallback to far future if no expiry date
		return new Date("2099-12-31")
	}

	// Helper function to sort by expiry date (soonest first)
	const sortByExpiry = (couponList: T[]): T[] => {
		return couponList.sort((a, b) => {
			const expiryA = getExpiryDate(a)
			const expiryB = getExpiryDate(b)
			return expiryA.getTime() - expiryB.getTime()
		})
	}

	// Helper function to check if coupon meets minimum subtotal requirement
	const meetsMinimumSubtotal = (coupon: T): boolean => {
		if (cartSubtotal === undefined) return true // No cart context, assume it meets requirement
		const minAmount = coupon.appliedDiscountRule?.minAmount
		if (!minAmount || minAmount <= 0) return true // No minimum requirement
		return cartSubtotal >= minAmount
	}

	// Tier 1: Usable coupons (web/shop, not disabled, and meets minimum subtotal)
	const usableCoupons = coupons.filter((c) => {
		const isApplicable =
			c.platformChannels?.includes("shop") ||
			c.platformChannels?.includes("web")
		const isNotStarted =
			c.couponTemplate?.startTime && c.couponTemplate.startTime > now
		return isApplicable && !isNotStarted && meetsMinimumSubtotal(c)
	})

	// Tier 2: Haven't met minimum subtotal (web/shop, not disabled, but cart doesn't meet minimum)
	const notMetMinimumCoupons = coupons.filter((c) => {
		const isApplicable =
			c.platformChannels?.includes("shop") ||
			c.platformChannels?.includes("web")
		const isNotStarted =
			c.couponTemplate?.startTime && c.couponTemplate.startTime > now
		return isApplicable && !isNotStarted && !meetsMinimumSubtotal(c)
	})

	// Tier 3: Not started coupons (web/shop but not yet started)
	const notStartedCoupons = coupons.filter((c) => {
		const isApplicable =
			c.platformChannels?.includes("shop") ||
			c.platformChannels?.includes("web")
		const isNotStarted =
			c.couponTemplate?.startTime && c.couponTemplate.startTime > now
		return isApplicable && isNotStarted
	})

	// Tier 4: App-only and other non-applicable coupons
	const nonApplicableCoupons = coupons.filter((c) => {
		const isApplicable =
			c.platformChannels?.includes("shop") ||
			c.platformChannels?.includes("web")
		return !isApplicable
	})

	return [
		...sortByExpiry(usableCoupons),
		...sortByExpiry(notMetMinimumCoupons),
		...sortByExpiry(notStartedCoupons),
		...sortByExpiry(nonApplicableCoupons),
	]
}
